import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Paper,
  Container,
  styled,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Chip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import AppsIcon from "@mui/icons-material/Apps";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useGetCapabilitiesQuery, useModuleQuery, useProductQuery } from "../../useQuery";
import useGetAppsQuery from "../../useQuery/useGetAppsQuery";
import { useLocation } from "react-router-dom";
import { HelpIconButton } from "../../components-new/help/help-modal";
import { APPIUM_INSPECTOR_URL, API_BASE_PATH, API_BASE_RELATIVE_PATH } from "../../Config/Config";
import apiFetch from "../../utils/apiFetch";

const StyledButton = styled(Button)`
  padding: 12px 32px;
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  font-size: 15px;
  transition: all 0.2s ease;
  box-shadow: none;

  &:hover {
    box-shadow: none;
  }

  &:disabled {
    background-color: #e5e7eb;
    color: #9ca3af;
  }
`;

const StyledSelect = styled(Select)`
  & .MuiOutlinedInput-notchedOutline {
    border-color: #d1d5db;
    transition: all 0.15s ease;
  }
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: #9ca3af;
  }
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #6b7ff5;
    border-width: 2px;
  }
  &.Mui-disabled {
    background-color: #f9fafb;
    & .MuiOutlinedInput-notchedOutline {
      border-color: #e5e7eb;
    }
  }
  & .MuiSelect-select {
    padding: 11px 14px;
    font-size: 14px;
    color: #374151;
  }
  border-radius: 6px;
  background-color: #ffffff;
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    border-radius: 6px;
    background-color: #ffffff;
    & fieldset {
      border-color: #d1d5db;
      transition: all 0.15s ease;
    }
    &:hover fieldset {
      border-color: #9ca3af;
    }
    &.Mui-focused fieldset {
      border-color: #6b7ff5;
      border-width: 2px;
    }
    &.Mui-error fieldset {
      border-color: #ef4444;
    }
  }
  & .MuiInputBase-input {
    padding: 11px 14px;
    font-size: 14px;
    color: #374151;
    &::placeholder {
      color: #9ca3af;
      opacity: 1;
    }
  }
`;

const StyledCard = React.memo(({ children, header, headerIcon }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        background: "#ffffff",
      }}
    >
      {header && (
        <Box
          sx={{
            px: 4,
            py: 3,
            background: "#6b7ff5",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {headerIcon && (
              <Box
                sx={{
                  mr: 2,
                  p: 1.2,
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {React.cloneElement(headerIcon, {
                  sx: { fontSize: 22, color: "#ffffff" },
                })}
              </Box>
            )}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "20px",
                  mb: 0.3,
                }}
              >
                {header}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: "13px",
                }}
              >
                Configure your mobile test automation session
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{ p: 4 }}>{children}</Box>
    </Paper>
  );
});

StyledCard.displayName = "StyledCard";

const FormField = React.memo(({ label, tooltip, icon, required, children }) => {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        {icon && (
          <Box
            sx={{
              mr: 1,
              display: "flex",
              alignItems: "center",
              color: "#6b7ff5",
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 16 } })}
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: "20px",
            color: "#374151",
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
              *
            </Box>
          )}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <IconButton size="small" sx={{ ml: 0.5, p: 0.3, color: "#9ca3af" }}>
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {children}
    </Box>
  );
});

FormField.displayName = "FormField";

const MobileAutomation = () => {
  const path = useLocation().pathname;

  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedCapability, setSelectedCapability] = useState("");
  const [selectedAppVersion, setSelectedAppVersion] = useState("");
  const [testScenarioName, setTestScenarioName] = useState("");
  const [noReset, setNoReset] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const [scenarioCheckStatus, setScenarioCheckStatus] = useState(null);

  const debounceTimerRef = React.useRef(null);

  const { data: products } = useProductQuery();
  const { data: modules } = useModuleQuery(selectedProduct, {
    enabled: !!selectedProduct,
  });
  const { data: savedCapabilities } = useGetCapabilitiesQuery();
  const {
    data: apps,
    isLoading: appsLoading,
    error: appsError,
  } = useGetAppsQuery(selectedProduct, {
    enabled: !!selectedProduct,
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem("userDetail") || localStorage.getItem("userDetails");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const t = parsed?.token || parsed?.accessToken || parsed?.access_token || "";
      if (t) setToken(t);
    } catch (err) {
      console.error("Error retrieving token:", err);
    }
  }, []);

  useEffect(() => {
    setSelectedModule("");
    setSelectedCapability("");
    setSelectedAppVersion("");
  }, [selectedProduct]);

  useEffect(() => {
    setSelectedCapability("");
    setSelectedAppVersion("");
    setScenarioCheckStatus(null);
  }, [selectedModule]);

  const checkTestScenarioExists = useCallback(async (name, moduleId, authToken) => {
    if (!name.trim() || !moduleId || !authToken) {
      setScenarioCheckStatus(null);
      return;
    }

    try {
      const raw = localStorage.getItem("userDetail") || localStorage.getItem("userDetails") || "{}";
      const userDetails = JSON.parse(raw);
      const companyId =
        userDetails?.companyid || userDetails?.companyId || userDetails?.company_id || "";

      const params = new URLSearchParams({
        action: "checkForTestCaseInModule",
        companyid: companyId,
        testCase: name.trim(),
        module: moduleId,
      });

      const response = await apiFetch(
        `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?${params.toString()}`,
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setScenarioCheckStatus(data.exists ? "exists" : "available");
    } catch (err) {
      console.error("Error checking scenario:", err);
      setScenarioCheckStatus("error");
    }
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!testScenarioName.trim() || !selectedModule || !token) {
      setScenarioCheckStatus(null);
      return;
    }

    setScenarioCheckStatus("loading");

    debounceTimerRef.current = setTimeout(() => {
      checkTestScenarioExists(testScenarioName, selectedModule, token);
    }, 600);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [testScenarioName, selectedModule, token, checkTestScenarioExists]);

  const scenarioFieldAdornment = useMemo(() => {
    if (!testScenarioName.trim() || !selectedModule) return null;

    if (scenarioCheckStatus === "loading") {
      return (
        <InputAdornment position="end">
          <CircularProgress size={16} sx={{ color: "#6b7ff5" }} thickness={4} />
        </InputAdornment>
      );
    }
    if (scenarioCheckStatus === "exists") {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon sx={{ fontSize: 18, color: "#ef4444" }} />
        </InputAdornment>
      );
    }
    if (scenarioCheckStatus === "available") {
      return (
        <InputAdornment position="end">
          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#10b981" }} />
        </InputAdornment>
      );
    }
    return null;
  }, [testScenarioName, selectedModule, scenarioCheckStatus]);

  const scenarioHelperText = useMemo(() => {
    if (!testScenarioName.trim() || !selectedModule) return "";
    if (scenarioCheckStatus === "loading") return "Checking availability...";
    if (scenarioCheckStatus === "exists")
      return "A test scenario with this name already exists in the selected module.";
    if (scenarioCheckStatus === "available") return "Test scenario name is available.";
    if (scenarioCheckStatus === "error") return "Could not verify name. Proceed with caution.";
    return "";
  }, [testScenarioName, selectedModule, scenarioCheckStatus]);

  const scenarioHelperColor = useMemo(() => {
    if (scenarioCheckStatus === "exists") return "#ef4444";
    if (scenarioCheckStatus === "available") return "#10b981";
    if (scenarioCheckStatus === "error") return "#f59e0b";
    return "#6b7280";
  }, [scenarioCheckStatus]);

  const handleOpenAppium = useCallback(async () => {
    if (!token) {
      setError("Authentication token not found. Please log in again.");
      return;
    }

    if (
      !selectedProduct ||
      !selectedModule ||
      !testScenarioName.trim() ||
      !selectedCapability ||
      !selectedAppVersion
    ) {
      setError("Please fill in all required fields before starting a new recording.");
      return;
    }

    const inspectorWindow = window.open("", "_blank");

    try {
      const response = await apiFetch(`${API_BASE_PATH}/KeyLogging/handoff`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product: selectedProduct,
          module: selectedModule,
          testScenario: testScenarioName,
          continueRecording: "false",
          capabilityId: selectedCapability,
          appId: selectedAppVersion,
          noReset: noReset.toString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Handoff request failed: ${response.status}`);
      }

      const { code } = await response.json();
      if (!code) {
        throw new Error("Handoff response contained no code.");
      }

      const url = `${APPIUM_INSPECTOR_URL}?handoff=${encodeURIComponent(code)}`;
      if (inspectorWindow) {
        inspectorWindow.location = url;
      } else {
        window.open(url, "_blank");
      }
    } catch (e) {
      console.error("Error opening Appium:", e);
      inspectorWindow?.close();
      setError("Could not start the recording session. Please try again.");
    }
  }, [
    token,
    selectedProduct,
    selectedModule,
    testScenarioName,
    selectedCapability,
    selectedAppVersion,
    noReset,
  ]);

  const getCapabilityDisplayName = useCallback((capability) => {
    const capData = capability.capability || {};
    const platform = capData["platformName"] || "Unknown";
    const device = capData["deviceName"] || "Unknown Device";
    const version = capData["platformVersion"] || "Unknown Version";

    return (
      <Box sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1f2937" }}>
          {capability.name || "Configuration"}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.3 }}>
          {platform} {version} • {device}
        </Typography>
      </Box>
    );
  }, []);

  const getAppVersionDisplayName = useCallback((app) => {
    const fileName = app.appLocation
      ? app.appLocation.split("/").pop()
      : app.appPackage || "Unknown";
    const isLatest = app.isLatestVersion?.toLowerCase() === "yes";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          py: 0.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, mr: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1f2937" }}>
            {app.appVersion}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.3, wordBreak: "break-all" }}>
            {fileName}
          </Typography>
        </Box>
        {isLatest && (
          <Chip
            label="LATEST"
            size="small"
            sx={{
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontSize: "9px",
              fontWeight: 700,
              height: "20px",
              letterSpacing: "0.5px",
            }}
          />
        )}
      </Box>
    );
  }, []);

  const isStartDisabled = useMemo(
    () =>
      !selectedModule ||
      !testScenarioName.trim() ||
      !selectedCapability ||
      !selectedAppVersion ||
      scenarioCheckStatus === "exists" ||
      scenarioCheckStatus === "loading" ||
      scenarioCheckStatus === "error",
    [selectedModule, testScenarioName, selectedCapability, selectedAppVersion, scenarioCheckStatus],
  );

  const menuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          maxHeight: 300,
          borderRadius: "8px",
          mt: 0.5,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        },
      },
    }),
    [],
  );

  return (
    <Box
      sx={{
        height: "100vh",
        background: "#f3f4f6",
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ height: "100%", display: "flex", flexDirection: "column", py: 3 }}
      >
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#111827",
                fontSize: "28px",
                mb: 0.5,
              }}
            >
              Mobile Automation
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "14px" }}>
              Create and configure mobile test automation sessions with Appium Inspector
            </Typography>
          </Box>
          <HelpIconButton path={path} />
        </Box>

        <Box sx={{ mb: 3 }}>
          {selectedCapability && selectedAppVersion ? (
            <Alert
              severity="success"
              icon={<CheckCircleOutlineIcon />}
              sx={{
                borderRadius: "8px",
                backgroundColor: "#d1fae5",
                border: "1px solid #86efac",
                "& .MuiAlert-icon": { color: "#059669" },
                py: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "#1f2937", fontSize: "14px" }}
              >
                Configuration complete! Ready to start your recording session.
              </Typography>
            </Alert>
          ) : (
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              sx={{
                borderRadius: "8px",
                backgroundColor: "#e8eaff",
                border: "1px solid #6b7ff5",
                "& .MuiAlert-icon": { color: "#6b7ff5" },
                py: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "#1f2937", fontSize: "14px" }}
              >
                Complete all required fields to start your automation session
              </Typography>
            </Alert>
          )}
        </Box>

        <Box sx={{ flex: "0 1 auto", overflow: "auto" }}>
          <StyledCard>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
                mb: 3.5,
              }}
            >
              <FormField label="Product" icon={<AppsIcon />} required>
                <StyledSelect
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  displayEmpty
                  fullWidth
                  MenuProps={menuProps}
                >
                  <MenuItem disabled value="" sx={{ fontSize: "14px", color: "#9ca3af" }}>
                    Select a product
                  </MenuItem>
                  {products?.map((product) => (
                    <MenuItem
                      key={product.idproducts}
                      value={product.idproducts}
                      sx={{
                        fontSize: "14px",
                        py: 1.5,
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                    >
                      {product.productname}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormField>

              <FormField label="Module" icon={<AccountTreeIcon />} required>
                <StyledSelect
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  displayEmpty
                  fullWidth
                  disabled={!selectedProduct}
                  MenuProps={menuProps}
                >
                  <MenuItem disabled value="" sx={{ fontSize: "14px", color: "#9ca3af" }}>
                    Select a module
                  </MenuItem>
                  {modules?.map((module) => (
                    <MenuItem
                      key={module.idmodules}
                      value={module.idmodules}
                      sx={{
                        fontSize: "14px",
                        py: 1.5,
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                    >
                      {module.modulename}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormField>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
                mb: 3.5,
              }}
            >
              <FormField label="Device Capability" icon={<PhoneAndroidIcon />} required>
                <StyledSelect
                  value={selectedCapability}
                  onChange={(e) => setSelectedCapability(e.target.value)}
                  displayEmpty
                  fullWidth
                  disabled={!selectedModule}
                  MenuProps={menuProps}
                >
                  <MenuItem disabled value="" sx={{ fontSize: "14px", color: "#9ca3af" }}>
                    Select device capability
                  </MenuItem>
                  {savedCapabilities?.map((capability) => (
                    <MenuItem
                      key={capability.CapId}
                      value={capability.CapId}
                      sx={{
                        py: 1.5,
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                    >
                      {getCapabilityDisplayName(capability)}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormField>

              <FormField label="App Version" icon={<AppsIcon />} required>
                <StyledSelect
                  value={selectedAppVersion}
                  onChange={(e) => setSelectedAppVersion(e.target.value)}
                  displayEmpty
                  fullWidth
                  disabled={
                    !selectedProduct || appsLoading || !selectedModule || !selectedCapability
                  }
                  MenuProps={menuProps}
                >
                  <MenuItem disabled value="" sx={{ fontSize: "14px", color: "#9ca3af" }}>
                    {appsLoading ? "Loading..." : "Select app version"}
                  </MenuItem>
                  {Array.isArray(apps) &&
                    apps.map((app) => (
                      <MenuItem
                        key={app.appId}
                        value={app.appId}
                        sx={{
                          py: 1.5,
                          "&:hover": { backgroundColor: "#f3f4f6" },
                        }}
                      >
                        {getAppVersionDisplayName(app)}
                      </MenuItem>
                    ))}
                </StyledSelect>
              </FormField>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <FormField label="Test Scenario Name" required>
                <StyledTextField
                  value={testScenarioName}
                  onChange={(e) => setTestScenarioName(e.target.value)}
                  placeholder="Enter test scenario name"
                  fullWidth
                  error={scenarioCheckStatus === "exists"}
                  InputProps={{
                    endAdornment: scenarioFieldAdornment,
                  }}
                />
                {scenarioHelperText && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.8,
                      display: "block",
                      color: scenarioHelperColor,
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {scenarioHelperText}
                  </Typography>
                )}
              </FormField>

              <FormField
                label="App Reset Behavior"
                tooltip="If disabled, app will reset before each test. If enabled, app continues from current state."
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "#fef9e7",
                    border: "1px solid #f7dc6f",
                    borderRadius: "6px",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={noReset}
                        onChange={(e) => setNoReset(e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#6b7ff5",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#6b7ff5",
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1f2937" }}>
                          {noReset ? "No Reset Mode" : "Reset Mode"}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.2 }}>
                          {noReset
                            ? "App continues from current state"
                            : "App restarts fresh for each session"}
                        </Typography>
                      </Box>
                    }
                    sx={{ margin: 0, width: "100%" }}
                  />
                </Paper>
              </FormField>
            </Box>

            <Box display="flex" justifyContent="flex-end">
              <StyledButton
                variant="contained"
                onClick={handleOpenAppium}
                disabled={isStartDisabled}
                startIcon={<PlayArrowIcon />}
                sx={{
                  background: isStartDisabled ? "#e5e7eb" : "#ff5722",
                  color: "#ffffff",
                  "&:hover": {
                    background: isStartDisabled ? "#e5e7eb" : "#e64a19",
                  },
                }}
              >
                Start Recording Session
              </StyledButton>
            </Box>
          </StyledCard>
        </Box>

        {(error || appsError) && (
          <Alert
            severity="error"
            sx={{
              mt: 3,
              borderRadius: "8px",
              backgroundColor: "#fee2e2",
              border: "1px solid #fca5a5",
              "& .MuiAlert-icon": { color: "#dc2626" },
            }}
            onClose={error ? () => setError("") : undefined}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#991b1b", fontSize: "14px" }}
            >
              {error || `Error loading app versions: ${appsError?.message || "Failed to fetch"}`}
            </Typography>
          </Alert>
        )}
      </Container>
    </Box>
  );
};

export default MobileAutomation;
